<script>
import { whichTransitionEnd } from '@/modules/animation-helpers';

const transitionEnd = whichTransitionEnd();

export default {
  name: 'Panel',
  props: {
    closeOnClick: {
      type: Boolean,
      default: true
    },
    position: {
      type: String,
      default: 'right',
      validator(value) {
        return ['left', 'right'].includes(value);
      }
    },
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['xs', 'sm', 'md', 'lg', 'xl'].includes(value);
      }
    },
    title: String
  },
  data() {
    return {
      isOpen: false
    };
  },
  methods: {
    show() {
      this.$nextTick(() => { // Prevent the original click from triggering the close handler
        this.$emit('show');
        this.isOpen = true;
        this.$refs.panel.addEventListener(transitionEnd, () => this.$emit('shown'), { once: true });
        this.$refs.overlay.addEventListener('click', this.handleClick);
        document.addEventListener('keydown', this.handleKeyDown);
      });
    },

    hide() {
      this.$emit('hide');
      this.isOpen = false;
      this.$refs.panel.addEventListener(transitionEnd, () => this.$emit('hidden'), { once: true });
      this.$refs.overlay.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleKeyDown);
    },

    handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.hide();
      }
    },

    handleClick(event) {
      if (this.closeOnClick) {
        const target = event.target;
        const panel = target.closest('.panel');
        if (!panel) {
          this.hide();
        }
      }
    }
  }
};
</script>

<template>
  <div>
    <!-- Overlay -->
    <div
      ref="overlay"
      :class="{
        'overlay': true,
        'is-open': isOpen === true,
      }"
    />

    <!-- Panel -->
    <div
      ref="panel"
      :class="{
        'panel': true,
        'is-left': position === 'left',
        'is-right': position === 'right',
        'is-open': isOpen === true,
        'is-xs': size === 'xs',
        'is-sm': size === 'sm',
        'is-md': size === 'md',
        'is-lg': size === 'lg',
        'is-xl': size === 'xl',
      }"
    >
      <div class="panel-header">
        <h2 v-if="title" class="title">
          {{ title }}
        </h2>
        <span class="close">
          <a href="#" @click.prevent="hide">
            <i class="el-icon-fa-times" />
          </a>
        </span>
      </div>
      <div class="panel-body">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import '@/styles/variables.scss';

  .overlay {
    display: none;
    position: fixed;
    z-index: $--index-popper - 11;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: $--color-white;
    opacity: 0;

    &.is-open {
      display: block;
    }
  }

  .panel {
    position: fixed;
    z-index: $--index-popper - 10;
    top: 0;
    bottom: 0;
    width: 100%;
    background: $--color-white;
    padding: 2rem;
    transition: 300ms transform cubic-bezier(0.645, 0.005, 0.350, 1.000);
    overflow: auto;

    &.is-left {
      right: auto;
      left: 0;
      transform: translateX(-100%);
    }

    &.is-right {
      right: 0;
      left: auto;
      transform: translateX(100%);
    }

    &.is-xs {
      max-width: 10rem;
    }

    &.is-sm {
      max-width: 20rem;
    }

    &.is-md {
      max-width: 30rem;
    }

    &.is-lg {
      max-width: 40rem;
    }

    &.is-xl {
      max-width: 50rem;
    }

    &.is-open {
      box-shadow: 0 0 2rem rgba($--color-black, .1);
      transform: translateX(0);
    }

    @include breakpoint('sm-and-down') {
      padding: 1rem;

      &.is-xs,
      &.is-sm,
      &.is-md,
      &.is-lg,
      &.is-xl {
        max-width: none;
      }
    }
  }

  .panel-header {
    display: flex;
    font-size: 1.5rem;
    margin-bottom: 2rem;

    @include breakpoint('sm-and-down') {
      margin-bottom: 1rem;
    }

    .title {
      flex: 1 1 auto;
      font-size: inherit;
      margin: 0;
    }

    .close {
      text-align: right;
      flex: 1 0 auto;

      a {
        display: inline-block;
        height: 100%;
        color: $--color-gray-40;
        padding: 0 .5rem;
        transition: .1s color;
      }

      a:hover {
        color: darken($--color-gray-40, 7.5%);
      }
    }
  }
</style>

